/**
 * BobbyProductGallery - Metafield & Variant Filter Compatible
 * FIX: Resolved page scroll getting trapped inside the gallery container on
 *      both mobile (touch) and desktop (wheel). Key changes:
 *
 *  1. Touch handler now measures angle of swipe — only intercepts clearly
 *     horizontal swipes (angle < 35°). Vertical swipes fall through to the
 *     browser so the page scrolls normally.
 *
 *  2. Wheel (desktop mousewheel/trackpad) handler: only hijacks predominantly
 *     horizontal wheel deltas; vertical wheel events are NOT prevented so the
 *     page scrolls freely.
 *
 *  3. goToSlide() uses scrollLeft on the container instead of scrollIntoView().
 *     scrollIntoView() can scroll the entire PAGE to keep the element in view,
 *     which is what caused the "stuck" feeling on desktop.
 *
 *  4. overscroll-behavior-x: contain is set in CSS (see .liquid file) so that
 *     once the gallery reaches its end it doesn't chain-scroll the page
 *     horizontally, while still letting vertical page scroll pass through.
 */
class BobbyProductGallery {
    constructor(container) {
        this.container = container;
        this.galleryContainer = container.querySelector('.bobby-gallery-container');
        
        this.currentSlide = 0;
        this.isMobile = window.innerWidth < 750;

        this.slideSelector = '.bobby-gallery-slide';
        this.thumbSelector = '.bobby-thumb';
        this.dotSelector = '.bobby-dot';

        this.init();
    }

    init() {
        this.updateVisibleElements();
        if (this.visibleSlides.length === 0) return;

        // 1. Thumb & Dot clicks
        this.container.addEventListener('click', (e) => {
            const trigger = e.target.closest(`${this.thumbSelector}, ${this.dotSelector}`);
            if (!trigger) return;
            e.preventDefault();

            const selector = trigger.classList.contains('bobby-thumb')
                ? `${this.thumbSelector}:not(.bobby-hidden)`
                : `${this.dotSelector}:not(.bobby-hidden)`;
            const visibleTriggers = Array.from(this.container.querySelectorAll(selector));
            const index = visibleTriggers.indexOf(trigger);
            if (index !== -1) this.goToSlide(index);
        });

        // 2. Touch swipe — horizontal-intent only
        this.addTouchSupport();

        // 3. Desktop wheel — horizontal-intent only
        this.addWheelSupport();

        // 4. Scroll sync (keep thumbs/dots in step with manual drag-scroll)
        this.addScrollSync();

        // 5. Variant change
        this.listenVariantChange();

        window.addEventListener('resize', () => {
            this.isMobile = window.innerWidth < 750;
        });
    }

    updateVisibleElements() {
        this.visibleSlides = Array.from(this.container.querySelectorAll(`${this.slideSelector}:not(.bobby-hidden)`));
        this.visibleThumbs = Array.from(this.container.querySelectorAll(`${this.thumbSelector}:not(.bobby-hidden)`));
        this.visibleDots   = Array.from(this.container.querySelectorAll(`${this.dotSelector}:not(.bobby-hidden)`));
    }

    /**
     * Scrolls the gallery container to the target slide using scrollLeft.
     * IMPORTANT: We do NOT use scrollIntoView() — that method can scroll the
     * entire page to bring the element into view, which freezes vertical page
     * scrolling from the user's perspective.
     */
    goToSlide(index, smooth = true) {
        this.updateVisibleElements();
        if (index < 0 || index >= this.visibleSlides.length) return;

        this.currentSlide = index;
        const targetSlide = this.visibleSlides[index];

        // Get offset of the target slide relative to the scroll container
        const containerLeft = this.galleryContainer.getBoundingClientRect().left;
        const slideLeft     = targetSlide.getBoundingClientRect().left;
        const scrollTarget  = this.galleryContainer.scrollLeft + (slideLeft - containerLeft);

        this.galleryContainer.scrollTo({
            left: scrollTarget,
            behavior: smooth ? 'smooth' : 'auto',
        });

        this.updateActiveUI();
    }

    updateActiveUI() {
        this.visibleThumbs.forEach((thumb, idx) => {
            thumb.classList.toggle('bobby-active', idx === this.currentSlide);
        });
        this.visibleDots.forEach((dot, idx) => {
            dot.classList.toggle('bobby-active', idx === this.currentSlide);
        });
    }

    /**
     * Touch support that distinguishes horizontal swipes from vertical scrolls.
     * We measure both axes on touchmove and only prevent default (hijack scroll)
     * when the gesture is clearly horizontal (angle < 35 degrees from horizontal).
     */
    addTouchSupport() {
        let startX = 0;
        let startY = 0;
        let intentDecided = false;  // have we locked the gesture direction?
        let isHorizontal  = false;  // is this gesture horizontal?

        this.galleryContainer.addEventListener('touchstart', (e) => {
            startX = e.touches[0].clientX;
            startY = e.touches[0].clientY;
            intentDecided = false;
            isHorizontal  = false;
        }, { passive: true });

        this.galleryContainer.addEventListener('touchmove', (e) => {
            if (!intentDecided) {
                const dx = Math.abs(e.touches[0].clientX - startX);
                const dy = Math.abs(e.touches[0].clientY - startY);

                // Need at least 5px of movement before deciding
                if (dx < 5 && dy < 5) return;

                // Angle from horizontal: atan(dy/dx) — horizontal if < 35°
                isHorizontal  = dx > dy * Math.tan(35 * Math.PI / 180);
                intentDecided = true;
            }

            // Only block page scroll for horizontal swipes
            if (isHorizontal) e.preventDefault();
        }, { passive: false }); // passive:false needed to allow preventDefault

        this.galleryContainer.addEventListener('touchend', (e) => {
            if (!isHorizontal) return; // vertical gesture — let page handle it

            const endX = e.changedTouches[0].clientX;
            const diffX = startX - endX;

            if (Math.abs(diffX) > 50) {
                this.goToSlide(diffX > 0 ? this.currentSlide + 1 : this.currentSlide - 1);
            }
        }, { passive: true });
    }

    /**
     * Desktop: intercept only horizontal wheel events so the trackpad horizontal
     * swipe navigates the gallery without stealing vertical page scroll.
     */
    addWheelSupport() {
        let wheelTimeout;

        this.galleryContainer.addEventListener('wheel', (e) => {
            const absX = Math.abs(e.deltaX);
            const absY = Math.abs(e.deltaY);

            // If vertical component dominates, let the page scroll — do nothing
            if (absY > absX) return;

            // Horizontal-dominant: navigate gallery slides
            e.preventDefault();

            clearTimeout(wheelTimeout);
            wheelTimeout = setTimeout(() => {
                if (e.deltaX > 30) {
                    this.goToSlide(this.currentSlide + 1);
                } else if (e.deltaX < -30) {
                    this.goToSlide(this.currentSlide - 1);
                }
            }, 50);
        }, { passive: false });
    }

    addScrollSync() {
        let scrollTimeout;
        this.galleryContainer.addEventListener('scroll', () => {
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(() => {
                this.updateVisibleElements();
                const containerLeft = this.galleryContainer.getBoundingClientRect().left;

                let closestIndex = 0;
                let minDistance  = Infinity;

                this.visibleSlides.forEach((slide, idx) => {
                    const distance = Math.abs(slide.getBoundingClientRect().left - containerLeft);
                    if (distance < minDistance) {
                        minDistance  = distance;
                        closestIndex = idx;
                    }
                });

                if (this.currentSlide !== closestIndex) {
                    this.currentSlide = closestIndex;
                    this.updateActiveUI();
                }
            }, 100);
        }, { passive: true });
    }

    listenVariantChange() {
        document.addEventListener('change', (e) => {
            if (e.target.name === 'id') {
                setTimeout(() => {
                    this.updateVisibleElements();
                    this.goToSlide(0, false);
                }, 150);
            }
        });
    }
}

// Global init
document.addEventListener('DOMContentLoaded', () => {
    const initGalleries = () => {
        document.querySelectorAll('.bobby-product-gallery:not([data-bobby-initialized])').forEach(el => {
            new BobbyProductGallery(el);
            el.setAttribute('data-bobby-initialized', 'true');
        });
    };

    initGalleries();
    document.addEventListener('shopify:section:load', initGalleries);
});
